/**
 * Jobs whose R2 artifacts were deleted together with a published build
 * (internal/compiler/plans/2026-10-14-hard-delete-builds.md; internal/api
 * migration 0020). private/auth's admin routes serve such a job as
 * `status: "deleted"` with the stored outcome in `outcome`
 * (jobsAdminRoutes.ts withDerivedStatus); the job row and its telemetry
 * (memory, failure class and cause, timings) are untouched.
 *
 * Every panel in this app (failure, memory, cache, siblings) reads the REAL
 * status, so a deleted job is normalized at the fetch boundary: `status` goes
 * back to the stored outcome and `artifacts_deleted_at` stays set. Only the
 * chip ("artifacts deleted") and the artifact links (gone) look at the flag.
 * Nothing therefore errors on a deleted job and its telemetry keeps showing.
 */

export interface MaybeDeleted {
  status: string;
  outcome?: string | null;
  artifacts_deleted_at?: number | null;
}

/** The real status back on a job served as "deleted"; flag kept. */
export function normalizeJobStatus<T extends MaybeDeleted>(job: T): T {
  if (job.status !== "deleted") return job;
  return {
    ...job,
    status: job.outcome ?? "failed",
    artifacts_deleted_at: job.artifacts_deleted_at ?? Date.now(),
  };
}

export function isArtifactsDeleted(job: {
  artifacts_deleted_at?: number | null;
}): boolean {
  return job.artifacts_deleted_at != null;
}

export const ARTIFACTS_DELETED_LABEL = "artifacts deleted";

export const ARTIFACTS_DELETED_NOTE =
  "The published build this job produced was deleted by an operator: its artifacts (binary / addon set, source, source maps, build log, failure bundle) are gone. The job record, memory telemetry and failure diagnostics are kept.";

/** What a status chip says: the status, or "artifacts deleted". */
export function statusChipLabel(
  status: string,
  job: { artifacts_deleted_at?: number | null },
): string {
  return isArtifactsDeleted(job) ? ARTIFACTS_DELETED_LABEL : status;
}
